/**
 * Vista previa de mi próxima alerta — Iter289 (feedback usuarios:
 * "que el delegado pueda ver el email que le llegaría antes de que se envíe").
 *
 * Reutiliza el mismo cálculo que el preview de admin, pero vía
 * GET /delegado/preview-my-digest, restringido al propio delegado logueado.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Mail, Sparkles, Clock3, CheckCircle2 } from "lucide-react";
import { API } from "@/context/AuthContext";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("es-ES"); } catch { return iso; }
};

export default function PreviewMiAlerta() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/delegado/preview-my-digest`)
      .then((r) => setData(r.data))
      .catch((e) => toast.error(e.response?.data?.detail || "No se pudo cargar la vista previa"))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="text-center py-16 text-slate-400" data-testid="preview-mi-alerta-loading">
        <Loader2 className="animate-spin mx-auto mb-2" size={24} /> Calculando tu próxima alerta…
      </div>
    );
  }

  if (!data) {
    return <p className="text-sm text-slate-400 text-center py-10" data-testid="preview-mi-alerta-empty">No se pudo calcular la vista previa.</p>;
  }

  return (
    <div className="space-y-4" data-testid="preview-mi-alerta-container">
      <div className={`rounded-xl p-4 border flex items-start gap-3 ${
        data.would_send_email ? "bg-emerald-50 border-emerald-200" : "bg-slate-50 border-slate-200"
      }`}>
        {data.would_send_email ? <Mail className="text-emerald-600 mt-0.5" size={18}/> : <CheckCircle2 className="text-slate-400 mt-0.5" size={18}/>}
        <div>
          <p className="text-sm font-bold text-slate-800" data-testid="preview-mi-alerta-status">
            {data.would_send_email
              ? `Hoy SÍ se te enviaría un email: ${data.nuevas_count} oportunidad(es) nueva(s)`
              : "Hoy NO se te enviaría ningún email (sin oportunidades nuevas en tu zona)"}
          </p>
          <p className="text-xs text-slate-500 mt-1">
            Zona: {(data.provincias || data.comunidades || []).join(", ") || "sin zona asignada"} ·
            {" "}Score mínimo: {data.score_min} · {data.total_scope} oportunidad(es) en tu ámbito actual
          </p>
        </div>
      </div>

      {data.nuevas?.length > 0 && (
        <div>
          <p className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-2 flex items-center gap-1">
            <Sparkles size={12}/> Nuevas que incluiría el email ({data.nuevas_count})
          </p>
          <div className="space-y-1.5" data-testid="preview-mi-alerta-nuevas">
            {data.nuevas.map((o) => (
              <div key={o.external_id} className="bg-white border border-emerald-200 rounded-lg p-2.5 text-xs" data-testid={`preview-mi-alerta-nueva-${o.external_id}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="font-semibold text-slate-800 line-clamp-1">{o.title}</span>
                  <span className="shrink-0 font-bold text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded">{o.score}</span>
                </div>
                <span className="text-slate-400">Detectada: {fmtDate(o.created_at)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {data.pendientes?.length > 0 && (
        <div>
          <p className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-2 flex items-center gap-1">
            <Clock3 size={12}/> Ya avisadas, aún pendientes de revisar ({data.pendientes_count})
          </p>
          <div className="space-y-1.5" data-testid="preview-mi-alerta-pendientes">
            {data.pendientes.map((o) => (
              <div key={o.external_id} className="bg-white border border-slate-200 rounded-lg p-2.5 text-xs" data-testid={`preview-mi-alerta-pendiente-${o.external_id}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="font-semibold text-slate-700 line-clamp-1">{o.title}</span>
                  <span className="shrink-0 font-bold text-slate-500 bg-slate-50 px-1.5 py-0.5 rounded">{o.score}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {data.nuevas_count === 0 && data.pendientes_count === 0 && (
        <p className="text-sm text-slate-400 text-center py-6" data-testid="preview-mi-alerta-sin-oportunidades">
          No hay oportunidades activas en tu zona en este momento.
        </p>
      )}
    </div>
  );
}
